import { createSettingsParams, SettingsParamType } from '@wix/tpa-settings';
import { DataType } from '@wix/editor-platform-ooi-metadata-resolver';

export type DisplaySettingsParams = {
  showHeaderImage: SettingsParamType.Boolean;
  showHeaderTitle: SettingsParamType.Boolean;
  showHeaderDescription: SettingsParamType.Boolean;
  showMenuDescription: SettingsParamType.Boolean;
  showSectionTitle: SettingsParamType.Boolean;
  showSectionDescription: SettingsParamType.Boolean;
  showSectionImage: SettingsParamType.Boolean;
  showMenuDivider: SettingsParamType.Boolean;
  showItemImage: SettingsParamType.Boolean;
  showItemDescription: SettingsParamType.Boolean;
  showItemLabels: SettingsParamType.Boolean;
  showItemFeatured: SettingsParamType.Boolean;
  showAddToCartButton: SettingsParamType.Boolean;
  overrideItemDesign: SettingsParamType.Boolean;
};

/**
 * `metadata` exposes these params in the widget's editor manifest
 * (`editorElement.data`), which is what Aria reads and edits in Studio 2.
 * Params without `metadata` are dropped from the manifest, so Aria can't
 * change them. `displayName` reuses the Settings panel labels.
 */
export const displaySettingsParams =
  createSettingsParams<DisplaySettingsParams>({
    showHeaderImage: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        dataItemManifestData: {
          displayName: t('app.settings.display.header-image'),
        },
        ai: {
          description:
            'Shows (true) or hides (false) the cover image in the Online Orders header. ' +
            "Hiding it doesn't delete the image; the image itself is managed in the " +
            'restaurant settings. headerImagePlacement has no effect while the image is ' +
            'hidden. No single setting hides the whole header: set showHeaderImage, ' +
            'showHeaderTitle and showHeaderDescription to false. The location button and ' +
            'order status badges, when shown, cannot be hidden by any setting.',
        },
      }),
    },
    showHeaderTitle: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        dataItemManifestData: {
          displayName: t('app.settings.display.header-title'),
        },
        ai: {
          description:
            'Shows (true) or hides (false) the title at the top of the Online Orders widget ' +
            'header (default text "Online Ordering"). This is the only way to hide or show ' +
            'the title; clearing headerTitle does not hide it.',
        },
      }),
    },
    showHeaderDescription: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        dataItemManifestData: {
          displayName: t('app.settings.display.header-description'),
        },
        ai: {
          description:
            'Shows (true) or hides (false) the description text under the title in the ' +
            'Online Orders widget header. This is the only way to hide or show the ' +
            'description; clearing headerDescription does not hide it.',
        },
      }),
    },
    showMenuDescription: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        dataItemManifestData: {
          displayName: t('app.settings.display.menu-description'),
        },
        ai: {
          description:
            'Shows (true) or hides (false) the description shown under each menu name.',
        },
      }),
    },
    showSectionTitle: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        dataItemManifestData: {
          displayName: t('app.settings.display.section-title'),
        },
        ai: {
          description:
            'Shows (true) or hides (false) the title of each menu section (for example ' +
            '"Appetizers").',
        },
      }),
    },
    showSectionDescription: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        dataItemManifestData: {
          displayName: t('app.settings.display.section-description'),
        },
        ai: {
          description:
            'Shows (true) or hides (false) the description shown under each menu section ' +
            'title.',
        },
      }),
    },
    showSectionImage: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        dataItemManifestData: {
          displayName: t('app.settings.display.section-image'),
        },
        ai: {
          description:
            'Shows (true) or hides (false) the cover image of each menu section, when the ' +
            'section has one.',
        },
      }),
    },
    showMenuDivider: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        dataItemManifestData: {
          displayName: t('app.settings.display.menu-divider'),
        },
        ai: {
          description:
            'Shows (true) or hides (false) the divider line between the menu name and its ' +
            'sections.',
        },
      }),
    },
    showItemImage: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        dataItemManifestData: {
          displayName: t('app.settings.display.item-image'),
        },
        ai: {
          description:
            'Shows (true) or hides (false) the image of each menu item, when the item has ' +
            'one.',
        },
      }),
    },
    showItemDescription: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        dataItemManifestData: {
          displayName: t('app.settings.display.item-description'),
        },
        ai: {
          description:
            'Shows (true) or hides (false) the description of each menu item.',
        },
      }),
    },
    showItemLabels: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        dataItemManifestData: {
          displayName: t('app.settings.display.item-labels'),
        },
        ai: {
          description:
            'Shows (true) or hides (false) the dietary label icons of each menu item (for ' +
            'example vegan or spicy).',
        },
      }),
    },
    showItemFeatured: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        dataItemManifestData: {
          displayName: t('app.settings.design.section-header.highlighted-item'),
        },
        ai: {
          description:
            'Shows (true) or hides (false) the highlighted (featured) badge on menu items ' +
            'marked as featured. When false, featured items also lose the highlighted item ' +
            'design set by overrideItemDesign.',
        },
      }),
    },
    showAddToCartButton: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => true,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: true },
        dataItemManifestData: {
          displayName: t('app.settings.display.add-to-cart-button'),
        },
        ai: {
          description:
            'Shows (true) or hides (false) the add to cart button on menu items. When ' +
            'hidden, customers open the item to add it to the cart. This is the only way to ' +
            'hide the button; clearing addToCartButtonText does not hide it. Has no effect ' +
            'on sites where the quick add to cart button is not available.',
        },
      }),
    },
    overrideItemDesign: {
      type: SettingsParamType.Boolean,
      getDefaultValue: () => false,
      metadata: ({ t }) => ({
        type: DataType.booleanValue,
        defaultValue: { type: 'static', value: false },
        dataItemManifestData: {
          displayName: t(
            'app.settings.design.highlighted-item.override-item-design',
          ),
        },
        ai: {
          description:
            'When true, highlighted (featured) menu items use their own custom design ' +
            '(background, borders, corners, colors and fonts) instead of the regular item ' +
            'design. Only has an effect when showItemFeatured is true. Default false.',
        },
      }),
    },
  });
